import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Tag } from "primereact/tag";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import {
  apiService,
  RedTeamTenantListItem,
} from "../../services/apiService";
import { useToast } from "../../contexts/ToastContext";

function formatReservedAt(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

const Tenants: React.FC = () => {
  const [tenants, setTenants] = useState<RedTeamTenantListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"all" | "available" | "in_use">("all");
  const [filter, setFilter] = useState("");
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    const res = await apiService.getRedTeamTenants({ status });
    if (res.success) setTenants(res.data || []);
    else toast.showError("Failed", res.message || "Unable to load tenants");
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((tenant) => {
      const parts = [
        tenant.tenant_id,
        tenant.tenant_name,
        tenant.tenant_tf_name,
        tenant.comment || "",
        ...tenant.reservations.flatMap((r) => [
          r.project_reference,
          r.project_name,
        ]),
      ];
      return parts.join(" ").toLowerCase().includes(q);
    });
  }, [tenants, filter]);

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3 align-items-center justify-content-between">
        <h2 className="m-0 text-xl">Tenants</h2>
        <div className="flex flex-wrap gap-2">
          <Dropdown
            value={status}
            options={[
              { label: "All", value: "all" },
              { label: "Available", value: "available" },
              { label: "In use", value: "in_use" },
            ]}
            onChange={(e) => setStatus(e.value)}
            placeholder="Status"
          />
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter tenants"
            />
          </IconField>
          <Button
            icon="pi pi-refresh"
            className="p-button-outlined"
            onClick={load}
            loading={loading}
          />
        </div>
      </div>

      <DataTable
        value={filtered}
        loading={loading}
        paginator
        rows={20}
        emptyMessage="No tenants found"
        size="small"
      >
        <Column field="tenant_name" header="Name" sortable />
        <Column field="tenant_id" header="Tenant ID" sortable />
        <Column field="tenant_tf_name" header="TF name" sortable />
        <Column
          header="Type"
          body={(row: RedTeamTenantListItem) =>
            [
              row.relay_tenant ? "relay" : null,
              row.phishing_tenant ? "phishing" : null,
            ]
              .filter(Boolean)
              .join(", ") || "—"
          }
        />
        <Column
          header="Status"
          body={(row: RedTeamTenantListItem) =>
            row.in_use ? (
              <Tag severity="warning" value="In use" />
            ) : (
              <Tag severity="success" value="Available" />
            )
          }
        />
        <Column
          header="Project"
          body={(row: RedTeamTenantListItem) => {
            if (!row.reservations?.length) return "—";
            return row.reservations
              .map((r) =>
                r.project_name
                  ? `${r.project_reference} — ${r.project_name}`
                  : r.project_reference,
              )
              .join("; ");
          }}
        />
        <Column
          header="In use since"
          body={(row: RedTeamTenantListItem) => {
            if (!row.reservations?.length) return "—";
            return row.reservations
              .map((r) => formatReservedAt(r.reserved_at))
              .join("; ");
          }}
        />
        <Column field="comment" header="Comment" />
      </DataTable>
    </div>
  );
};

export default Tenants;
